import {upperFirst} from 'lodash';

import type {Annotation} from '../types';

const QUOTE_WORD_LIMIT = 8;
const QUOTE_LEAD_WORDS = 5;

/** Marked text as spoken: a long selection is cut to its first words and "and more". */
export const quoteMarkedText = (text: string): string => {
  const words = text.trim().split(/\s+/, QUOTE_WORD_LIMIT + 1);
  if (words.length <= QUOTE_WORD_LIMIT) {
    return words.join(' ');
  }
  return `${words.slice(0, QUOTE_LEAD_WORDS).join(' ')} and more`;
};

/** A mark's style as spoken: "yellow highlight" or "underline". */
export const styleName = (annotation: Annotation): string =>
  annotation.type === 'underline' ? 'underline' : `${annotation.color} highlight`;

export const describeAnnotationMode = (isOn: boolean): string =>
  isOn
    ? 'Highlights & Notes on. Answer choices are locked.'
    : 'Highlights & Notes off. You can choose an answer.';

const styleNameAtStart = (mark: Annotation): string => upperFirst(styleName(mark));

export const describeMarkAdded = (mark: Annotation, subject: string): string =>
  `${styleNameAtStart(mark)} added: ${subject}`;

export const describeMarkExtended = (mark: Annotation, subject: string): string =>
  `${styleNameAtStart(mark)} extended: now covers ${subject}`;

/** `notedCount` is how many of the merged marks carried a note, the survivor included. */
export const describeMarksMerged = (
  survivor: Annotation,
  notedCount: number,
  subject: string
): string => {
  if (notedCount >= 2) {
    return `Highlights and notes combined into one ${styleName(survivor)}: ${subject}`;
  }
  if (notedCount === 1) {
    return `Highlights combined into one ${styleName(survivor)}. Note kept: ${subject}`;
  }
  return `Highlights combined into one ${styleName(survivor)}: ${subject}`;
};

export const describeMarkRemoved = (mark: Annotation, subject: string): string =>
  `${styleNameAtStart(mark)}${mark.note ? ' and its note' : ''} removed: ${subject}`;

export const describeMarkUnchanged = (mark: Annotation): string =>
  mark.type === 'underline'
    ? 'Already underlined. Nothing changed.'
    : `Already highlighted in ${mark.color}. Nothing changed.`;

/** Null when the style picked is the one the mark already had. */
export const describeStyleChange = (
  before: Annotation,
  after: Annotation,
  subject: string
): string | null => {
  const from = styleName(before);
  const to = styleName(after);
  if (from === to) {
    return null;
  }
  if (before.type === 'highlight' && after.type === 'highlight') {
    return `Highlight changed from ${before.color} to ${after.color}: ${subject}`;
  }
  return `${upperFirst(from)} changed to ${to}: ${subject}`;
};

export const describeNoteAdded = (mark: Annotation, subject: string): string =>
  `Note added to ${styleName(mark)}: ${subject}`;

export const describeNoteRemoved = (mark: Annotation, subject: string): string =>
  `Note removed from ${styleName(mark)}: ${subject}`;
